import React, { useState } from 'react';
import {
  Shield,
  RefreshCw,
  LogOut,
  Radio,
  Users,
  Pickaxe,
  Sliders,
  CreditCard,
  Activity,
  LayoutDashboard,
  Menu,
  X,
  User,
} from 'lucide-react';
import type { AdminAccountDto } from '@nova-mine/shared';

export type AdminTab = 'dashboard' | 'users' | 'miners' | 'settings' | 'payments' | 'activity';

interface AdminHeaderProps {
  currentTab: AdminTab;
  onSelectTab: (tab: AdminTab) => void;
  admin: AdminAccountDto | null;
  onLogout: () => void;
  onRefresh: () => void;
  isLoading: boolean;
}

export const AdminHeader: React.FC<AdminHeaderProps> = ({
  currentTab,
  onSelectTab,
  admin,
  onLogout,
  onRefresh,
  isLoading,
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const tabs: Array<{ id: AdminTab; label: string; icon: React.FC<{ className?: string }> }> = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'miners', label: 'Miners', icon: Pickaxe },
    { id: 'settings', label: 'Reward Settings', icon: Sliders },
    { id: 'payments', label: 'Stars Payments', icon: CreditCard },
    { id: 'activity', label: 'Activity & Audit', icon: Activity },
  ];

  return (
    <header className="sticky top-0 z-40 w-full glass-panel border-b border-white/5 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between gap-4">
        {/* Brand / Logo */}
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-purple-600 via-indigo-600 to-cyan-500 flex items-center justify-center shadow-lg shadow-purple-600/20">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <span className="text-base font-black tracking-wider text-white">NOVA MINE</span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30 uppercase tracking-wider">
                Admin Console
              </span>
            </div>
            <p className="text-[11px] text-gray-400 flex items-center space-x-1.5 mt-0.5">
              <Radio className="w-2.5 h-2.5 text-emerald-400 animate-pulse" />
              <span>Edge & Turso Synced</span>
            </p>
          </div>
        </div>

        {/* Desktop Navigation Tabs */}
        <nav className="hidden lg:flex items-center space-x-1 bg-space-950/70 border border-white/10 p-1 rounded-2xl">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = currentTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => onSelectTab(tab.id)}
                className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                  active
                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md shadow-purple-600/30'
                    : 'text-gray-400 hover:text-white hover:bg-space-850'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Right Controls */}
        <div className="flex items-center space-x-2">
          {/* Refresh Button */}
          <button
            onClick={onRefresh}
            disabled={isLoading}
            className="p-2 rounded-xl bg-space-850 hover:bg-space-800 text-gray-300 hover:text-white border border-white/10 transition-all flex items-center justify-center"
            title="Refresh Server Metrics"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin text-cyan-400' : ''}`} />
          </button>

          {/* Admin User Profile */}
          {admin && (
            <div className="hidden sm:flex items-center space-x-2 bg-space-900 border border-white/10 px-3 py-1.5 rounded-xl text-xs">
              <User className="w-3.5 h-3.5 text-purple-400" />
              <span className="font-mono text-gray-200 font-bold">{admin.username}</span>
              <span className="text-[10px] uppercase font-bold text-nova-gold px-1.5 py-0.2 rounded bg-amber-500/10 border border-amber-500/20">
                {admin.role}
              </span>
            </div>
          )}

          {/* Logout Button */}
          <button
            onClick={onLogout}
            className="p-2 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 transition-all"
            title="Sign Out / Revoke Session"
          >
            <LogOut className="w-4 h-4" />
          </button>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-xl bg-space-850 text-gray-300 border border-white/10"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileMenuOpen && (
        <div className="lg:hidden px-4 pb-4 border-t border-white/5 bg-space-950/95 backdrop-blur-2xl">
          <div className="grid grid-cols-2 gap-1.5 pt-3">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const active = currentTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    onSelectTab(tab.id);
                    setMobileMenuOpen(false);
                  }}
                  className={`flex items-center space-x-2 p-2.5 rounded-xl text-xs font-bold transition-all ${
                    active
                      ? 'bg-purple-600 text-white'
                      : 'bg-space-900 text-gray-300 hover:text-white border border-white/5'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </header>
  );
};
